import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';

interface FactFindRow {
  id: string;
  sourceApp: string;
  sourceReferenceId: string;
  factFindType: 'individual' | 'joint';
  submittedAt: string;
  payload: {
    scope?: { factFindType?: string; adviceAreas?: string[]; prompt?: string };
    primaryClient?: { firstName?: string; lastName?: string };
    partner?: { firstName?: string; lastName?: string };
    employment?: { annualTaxableIncome?: string | number; occupation?: string };
    riskProfile?: { profile?: string };
    superannuation?: { fundsList?: Array<{ name?: string; balance?: string | number }> };
    assets?: { assetsList?: Array<{ type?: string; value?: string | number }> };
  };
  pdfUrl?: string | null;
  status: string;
  dedupeOutcome?: string | null;
  matchCandidateId?: string | null;
}

const ADVICE_AREA_LABEL: Record<string, string> = {
  retirement_planning: 'Retirement planning',
  retirement_income: 'Retirement income',
  superannuation: 'Superannuation',
  investment: 'Investment',
  estate: 'Estate planning',
  insurance: 'Insurance',
  budget_cashflow: 'Budget / cash flow',
  debt: 'Debt strategy',
  other: 'Other',
};

const OUTCOME_LABEL: Record<string, { label: string; cls: string }> = {
  exact_match: { label: 'Attached to existing client', cls: 'bg-forest/20 text-forest' },
  fuzzy_queued: { label: 'New client created — duplicate queued for review', cls: 'bg-caution/20 text-caution' },
  no_match: { label: 'New client created', cls: 'bg-rust/10 text-rust' },
  mixed_match: { label: 'Mixed (one attached, one new)', cls: 'bg-brass/20 text-brass-deep' },
};

export function FactFindsTab({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['fact-finds', clientId],
    queryFn: () => api<{ data: FactFindRow[] }>(`/api/intake/fact-finds/by-client/${clientId}`),
  });

  if (isLoading) return <div className="mt-6"><QASkeleton variant="cards" rows={2} /></div>;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load fact-finds</div>;

  if (data.data.length === 0) {
    return (
      <div className="mt-6">
        <QAEmpty eyebrow="No fact-finds yet" title="Nothing submitted yet">
          When this client submits a fact-find via Fact-Find.AI, it'll land here automatically.
        </QAEmpty>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {data.data.map(ff => <FactFindCard key={ff.id} ff={ff} />)}
    </div>
  );
}

function FactFindCard({ ff }: { ff: FactFindRow }) {
  const p = ff.payload;
  const adviceAreas = p.scope?.adviceAreas ?? [];
  const income = parseNumber(p.employment?.annualTaxableIncome);
  const totalAssets = (p.assets?.assetsList ?? []).reduce((s, a) => s + (parseNumber(a.value) ?? 0), 0);
  const totalSuper = (p.superannuation?.fundsList ?? []).reduce((s, f) => s + (parseNumber(f.balance) ?? 0), 0);
  const outcome = ff.dedupeOutcome ? OUTCOME_LABEL[ff.dedupeOutcome] : null;

  return (
    <div className="qa-card">
      <div className="flex items-center justify-between px-4 py-2 border-b border-hairline-paper text-xs qa-mono uppercase">
        <div className="flex items-center gap-3">
          <span className="text-ink-mute">submitted</span>
          <span className="text-ink">{formatDateAU(ff.submittedAt)}</span>
          <span className="text-ink-mute">·</span>
          <span className="text-ink-mute">{ff.factFindType}</span>
          <span className="text-ink-mute">·</span>
          <span className="text-ink-mute">via</span>
          <span className="text-ink">{ff.sourceApp}</span>
        </div>
        {outcome && <span className={`px-2 py-0.5 ${outcome.cls}`}>{outcome.label}</span>}
      </div>

      {ff.dedupeOutcome === 'fuzzy_queued' && ff.matchCandidateId && (
        <Link to={`/admin/match-candidates?focus=${ff.matchCandidateId}`}
          className="block px-4 py-2 bg-caution/10 border-b border-hairline-paper text-xs text-caution hover:bg-caution/20">
          A possible duplicate client was queued from this submission — review the match →
        </Link>
      )}

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {adviceAreas.length > 0 && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs mr-2">Advice areas</span>
            <span className="text-ink">{adviceAreas.map(a => ADVICE_AREA_LABEL[a] ?? a).join(', ')}</span>
          </div>
        )}
        {p.scope?.prompt && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs">Client's prompt</span>
            <p className="text-ink mt-1 italic">"{p.scope.prompt}"</p>
          </div>
        )}
        {income != null && (
          <Stat label="Taxable income" value={formatAUD(income)} />
        )}
        {totalAssets > 0 && (
          <Stat label="Total assets (declared)" value={formatAUD(totalAssets)} />
        )}
        {totalSuper > 0 && (
          <Stat label="Super balance (combined)" value={formatAUD(totalSuper)} />
        )}
        {p.riskProfile?.profile && (
          <Stat label="Self-assessed risk" value={p.riskProfile.profile} />
        )}
        {p.employment?.occupation && (
          <Stat label="Occupation" value={p.employment.occupation} />
        )}
      </div>

      <div className="border-t border-hairline-paper px-4 py-2 flex items-center justify-between text-xs">
        <span className="text-ink-mute qa-mono">ref: {ff.sourceReferenceId}</span>
        {ff.pdfUrl ? (
          <a href={ff.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">↓ View PDF</a>
        ) : (
          <span className="text-ink-mute italic">PDF not attached</span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="qa-eyebrow text-xs block">{label}</span>
      <span className="text-ink qa-mono">{value}</span>
    </div>
  );
}

function parseNumber(v: string | number | undefined | null): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[$,\s]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}
